import Box from '@mui/material/Box';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * ResponsiveHidden — drop-in replacement for MUI <Hidden /> (v5+)
 *
 * Supports:
 *  - xsUp, smUp, mdUp, lgUp, xlUp  → hide on that breakpoint and up
 *  - xsDown, smDown, mdDown, lgDown, xlDown  → hide on that breakpoint and down
 *
 * Usage:
 *  <ResponsiveHidden mdUp>Hidden on md & up</ResponsiveHidden>
 *  <ResponsiveHidden smDown>Hidden on sm & down</ResponsiveHidden>
 */
const ResponsiveHidden = ({
                            xsUp,
                            smUp,
                            mdUp,
                            lgUp,
                            xlUp,
                            xsDown,
                            smDown,
                            mdDown,
                            lgDown,
                            xlDown,
                            children,
                            ...rest
                          }) => {
  const theme = useTheme();

  // ✅ Always call hooks in the same order
  const matches = {
    xsUp: useMediaQuery(theme.breakpoints.up('xs')),
    smUp: useMediaQuery(theme.breakpoints.up('sm')),
    mdUp: useMediaQuery(theme.breakpoints.up('md')),
    lgUp: useMediaQuery(theme.breakpoints.up('lg')),
    xlUp: useMediaQuery(theme.breakpoints.up('xl')),
    xsDown: useMediaQuery(theme.breakpoints.down('xs')),
    smDown: useMediaQuery(theme.breakpoints.down('sm')),
    mdDown: useMediaQuery(theme.breakpoints.down('md')),
    lgDown: useMediaQuery(theme.breakpoints.down('lg')),
    xlDown: useMediaQuery(theme.breakpoints.down('xl')),
  };

  // ✅ Compute hidden status safely
  const hidden =
    (xsUp && matches.xsUp) ||
    (smUp && matches.smUp) ||
    (mdUp && matches.mdUp) ||
    (lgUp && matches.lgUp) ||
    (xlUp && matches.xlUp) ||
    (xsDown && matches.xsDown) ||
    (smDown && matches.smDown) ||
    (mdDown && matches.mdDown) ||
    (lgDown && matches.lgDown) ||
    (xlDown && matches.xlDown);

  if (hidden) {
    return null;
  }

  return <Box height={'100%'} {...rest}>{children}</Box>;
};

ResponsiveHidden.propTypes = {
  xsUp: PropTypes.bool,
  smUp: PropTypes.bool,
  mdUp: PropTypes.bool,
  lgUp: PropTypes.bool,
  xlUp: PropTypes.bool,
  xsDown: PropTypes.bool,
  smDown: PropTypes.bool,
  mdDown: PropTypes.bool,
  lgDown: PropTypes.bool,
  xlDown: PropTypes.bool,
  children: PropTypes.node,
};

export default ResponsiveHidden;
